import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Loader2, AlertTriangle, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SenatDinnerSuccess() {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 5;
  const processed = useRef(false);

  const sessionId = searchParams.get("session_id");

  const processPayment = useCallback(async (sid: string) => {
    if (processed.current) return;
    try {
      const { data, error: rpcError } = await supabase
        .rpc("mark_senat_dinner_paid", { session_id: sid } as any);

      if (rpcError) throw rpcError;

      const results = data as any[];
      if (!results || results.length === 0) {
        if (retryCount < maxRetries) {
          setTimeout(() => setRetryCount((p) => p + 1), (retryCount + 1) * 2000);
          return;
        }
        throw new Error("Inscription non trouvée");
      }

      const result = results[0];
      if (result.payment_status === "paid" || result.payment_status === "completed") {
        processed.current = true;
        setSuccess(true);
        try {
          await supabase.functions.invoke("send-senat-dinner-confirmation", {
            body: { registrationId: result.id, sessionId: sid },
          });
        } catch (e) {
          console.error("Email confirmation invocation error:", e);
        }
      } else {
        if (retryCount < maxRetries) {
          setTimeout(() => setRetryCount((p) => p + 1), (retryCount + 1) * 2000);
          return;
        }
        throw new Error("Le paiement n'a pas pu être confirmé");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    if (sessionId) {
      processPayment(sessionId);
    } else {
      setError("Session ID introuvable.");
      setIsLoading(false);
    }
  }, [sessionId, processPayment, retryCount]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F0F8] via-[#F5F8FC] to-[#E8F0F8] flex items-center justify-center">
      <div className="max-w-xl w-full mx-4">
        <div className="text-center mb-6">
          <Landmark className="h-10 w-10 text-[#00509E] mx-auto mb-2" />
          <h1 className="text-xl font-bold text-[#001233]">
            Dîner au Sénat — 16 juin 2026
          </h1>
        </div>
        <Card className="border-0 shadow-2xl">
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-center py-10">
                <Loader2 className="h-12 w-12 text-[#00509E] animate-spin mx-auto" />
                <p className="mt-4 text-lg text-foreground">Confirmation du paiement en cours...</p>
                {retryCount > 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Tentative {retryCount}/{maxRetries}...
                  </p>
                )}
              </div>
            ) : error ? (
              <div className="text-center py-10">
                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-500 text-lg mb-4">{error}</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Si le problème persiste, contactez-nous à contact@edlr.fr
                </p>
                <Button variant="outline" onClick={() => window.location.reload()}>
                  Réessayer
                </Button>
              </div>
            ) : (
              <div className="text-center py-10">
                <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
                <h2 className="text-3xl font-bold mb-2">Inscription confirmée !</h2>
                <p className="text-lg text-muted-foreground mb-4">
                  Votre place pour le dîner au Sénat est réservée.
                </p>
                <p className="text-muted-foreground mb-2">
                  📅 Mardi 16 juin 2026 à 19h15
                </p>
                <p className="text-muted-foreground mb-2">
                  📍 Palais du Luxembourg — 15, rue de Vaugirard, 75006 Paris
                </p>
                <p className="text-sm text-muted-foreground mt-4 italic">
                  Pour des raisons de sécurité, une pièce d'identité vous sera demandée à l'entrée. Tenue de ville.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Un reçu de paiement vous a été envoyé par Stripe à l'adresse email saisie lors du paiement.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
